import React from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/shared/ui'
import { ArrowRight, Mail, Menu, X, SendHorizonal } from 'lucide-react'
import { cn } from '@/lib/utils'
import pgHomeVideo from '@/images/video/video-pg-home.mp4'
import { getLogo } from '@/modules/branding/brandingService'
import { isLightTheme } from '@/lib/theme'
import { scrollToLandingSection } from '@/modules/landing/utils/landingSections'
import { useLandingSectionNavigation } from '@/modules/landing/hooks/useLandingSectionNavigation'
import type { LandingSectionId } from '@/modules/landing/utils/landingSections'

const menuItems: Array<{ name: string; section?: LandingSectionId; href?: string }> = [
  { name: 'Diferenciais', section: 'diferenciais' },
  { name: 'Monitoramento', section: 'monitoramento' },
  { name: 'Planos', section: 'pricing' },
  { name: 'D\u00favidas Frequentes', section: 'faq' },
]

export function LandingHeader() {
  const [open, setOpen] = React.useState(false)
  const { goToSection } = useLandingSectionNavigation()

  return (
    <header className="sticky top-0 z-30 w-full border-b border-[#121212] backdrop-blur supports-[backdrop-filter]:bg-background/70">
      <nav data-state={open ? 'active' : undefined} className="group">
        <div className="mx-auto max-w-[1550px] px-6">
          <div className="flex flex-wrap items-center justify-between gap-6 py-3 lg:gap-0 lg:py-4">
            <div className="flex w-full justify-between lg:w-auto">
              <Link to="/" aria-label="home" className="flex items-center space-x-2">
                <Logo />
              </Link>
              <button
                onClick={() => setOpen(!open)}
                aria-label={open ? 'Fechar menu' : 'Abrir menu'}
                className="relative z-20 -m-2.5 -mr-4 block cursor-pointer p-2.5 lg:hidden"
              >
                <Menu className="m-auto size-6 duration-200 group-data-[state=active]:rotate-180 group-data-[state=active]:scale-0 group-data-[state=active]:opacity-0" />
                <X className="absolute inset-0 m-auto size-6 -rotate-180 scale-0 opacity-0 duration-200 group-data-[state=active]:rotate-0 group-data-[state=active]:scale-100 group-data-[state=active]:opacity-100" />
              </button>
            </div>
            <div className="bg-background group-data-[state=active]:block lg:group-data-[state=active]:flex mb-6 hidden w-full flex-wrap items-center justify-end space-y-8 rounded-3xl border p-6 shadow-2xl shadow-zinc-300/20 md:flex-nowrap lg:m-0 lg:flex lg:w-fit lg:gap-6 lg:space-y-0 lg:border-transparent lg:bg-transparent lg:p-0 lg:shadow-none dark:shadow-none dark:lg:bg-transparent">
              <div className="lg:pr-4">
                <ul className="space-y-6 text-base lg:flex lg:gap-8 lg:space-y-0 lg:text-sm">
                  {[...menuItems, { name: 'Seja Afiliado', href: '/seja-afiliado' }].map((item, i) => (
                    <li key={i}>
                      {item.section ? (
                        <button
                          type="button"
                          onClick={() => {
                            setOpen(false)
                            goToSection(item.section as LandingSectionId)
                          }}
                          className="block text-left text-foreground/70 duration-150 hover:text-foreground"
                        >
                          {item.name}
                        </button>
                      ) : (
                        <Link
                          to={item.href as string}
                          className="block text-foreground/70 duration-150 hover:text-foreground"
                          onClick={() => setOpen(false)}
                        >
                          {item.name}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex w-full flex-col space-y-3 sm:flex-row sm:gap-3 sm:space-y-0 md:w-fit lg:border-l lg:pl-6">
                <Link
                  to="/login"
                  className="inline-flex h-9 items-center justify-center rounded-md border border-border bg-background px-3 text-sm font-medium hover:bg-muted"
                >
                  Login
                </Link>
                <a
                  href="#pricing"
                  onClick={(e) => {
                    e.preventDefault()
                    scrollToLandingSection('pricing')
                  }}
                  className="inline-flex h-9 items-center justify-center rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground hover:opacity-90"
                >
                  {'Come\u00e7ar agora!'}
                </a>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </header>
  )
}

export function HeroSection6() {
  return (
    <section className="relative w-full overflow-hidden min-h-[760px] sm:min-h-[840px] md:min-h-[820px] lg:min-h-[1000px] xl:min-h-[1080px]">
      {/* Right-side video occupying viewport width portion */}
      <div className="pointer-events-none absolute inset-y-0 right-0 hidden w-[65vw] md:w-[55vw] lg:w-[50vw] sm:block">
        <video
          src={pgHomeVideo as unknown as string}
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-x-0 top-0 h-3/4 w-full object-cover"
        />
        {/* Left fade to blend with background */}
        <div aria-hidden className="absolute inset-0 bg-gradient-to-r from-background via-background/60 to-transparent" />
        {/* Bottom fade to soften video edge */}
        <div
          aria-hidden
          className="absolute inset-x-0 bottom-0 h-24 sm:h-32 md:h-40 bg-gradient-to-t from-background/90 via-background/60 to-transparent"
        />
      </div>

      <div className="relative mx-auto max-w-[1550px] px-6 py-20 lg:py-16">
        <div className="lg:flex lg:items-center lg:gap-12">
          {/* Left content */}
          <div className="relative z-10 mx-auto max-w-2xl lg:max-w-3xl text-center lg:ml-0 lg:w-7/12 lg:text-left">
            <h1 className="mt-8 text-balance text-4xl font-semibold md:text-5xl xl:text-5xl">
              {'Seu radar 24/7 de alertas t\u00e9cnicos \u2014 mais clareza, menos ru\u00eddo.'}
            </h1>
            <p className="mt-6 text-base text-foreground/80 md:text-lg">
              {
                'O Cripto Alert monitora o mercado 24/7 e identifica cen\u00e1rios t\u00e9cnicos com contexto (indicadores e conflu\u00eancias) para apoiar sua an\u00e1lise e gest\u00e3o de risco com mais clareza.'
              }
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button
                onClick={() => scrollToLandingSection('pricing')}
                className="h-11 px-5 bg-primary text-primary-foreground"
              >
                {'Ver planos'}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
          {/* Spacer ensures layout balance on large screens (video is absolutely positioned) */}
          <div className="hidden lg:block lg:flex-1" />
        </div>
      </div>
      {/* Optional subtle global fade from left to right */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-gradient-to-r from-background/20 via-transparent to-transparent"
      />
      {/* Bottom fade across the whole hero to avoid a hard edge under the video */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-24 sm:h-32 md:h-40 bg-gradient-to-t from-background/95 via-background/70 to-transparent"
      />
    </section>
  )
}

export function Logo({ className }: { className?: string }) {
  const [url, setUrl] = React.useState<string | null>(null)
  React.useEffect(() => {
    ;(async () => setUrl(await getLogo('home', isLightTheme())))()
  }, [])
  if (url) return <img src={url} alt="logo" className={cn('h-[1.85rem] w-auto', className)} />
  return null
}
